import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money, dateShort } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const sel = "h-9 w-full rounded-sm border border-input bg-background px-2 text-sm";

function AssignDialog({ charge, reps, onClose, onDone }) {
  const [repId, setRepId] = useState("");
  const [key, setKey] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/votel/charges/${charge.id}/assign`, { rep_id: repId, product_key: key || null, reason });
      toast.success("Charge assigned — logged in audit trail"); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="assign-rep-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Assign rep manually</DialogTitle>
          <DialogDescription>Charge {charge.charge_id} · {money(charge.amount_cents / 100)}. Logged with your name and reason.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select className={sel} value={repId} onChange={(e) => setRepId(e.target.value)} data-testid="assign-rep-select">
            <option value="">Choose a rep…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Input placeholder="Product key (optional, e.g. $47 Website)" value={key} onChange={(e) => setKey(e.target.value)} data-testid="assign-product-key" />
          <Textarea placeholder="Reason (required) — e.g. sold by Kaden via the house checkout" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="assign-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !repId || reason.trim().length < 5} onClick={submit} data-testid="assign-confirm-btn">{busy ? "Assigning…" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const UnattributedCharges = ({ charges, reps, onChange }) => {
  const [active, setActive] = useState(null);
  return (
    <div className="rounded-sm border border-border bg-card p-5" data-testid="charges-card">
      <h3 className="font-heading font-semibold">Votel charges</h3>
      <p className="mb-3 text-xs text-muted-foreground">Unattributed / house = house revenue until an admin assigns a rep.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="charges-table">
          <thead><tr className="border-b border-border">
            {["Date", "Buyer", "Product / tier", "Amount", "Status", ""].map((c) => <th key={c} className="px-2 py-2 text-left overline">{c}</th>)}
          </tr></thead>
          <tbody>
            {charges.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-muted-foreground">No charges ingested yet.</td></tr>}
            {charges.map((c) => (
              <tr key={c.id} className="border-b border-border" data-testid={`charge-row-${c.charge_id}`}>
                <td className="px-2 py-2 font-mono text-xs">{dateShort(c.charge_created_at)}</td>
                <td className="px-2 py-2"><div>{c.buyer_name || c.customer_id || "—"}</div><div className="text-xs text-muted-foreground">{c.buyer_business}</div></td>
                <td className="px-2 py-2 font-mono text-xs">{c.product_name}<div className="text-muted-foreground">{c.product_id}</div></td>
                <td className="px-2 py-2 font-mono">{money(c.amount_cents / 100)}{c.refunded_cents > 0 && <div className="text-xs text-destructive">-{money(c.refunded_cents / 100)}</div>}</td>
                <td className="px-2 py-2"><StatusBadge status={c.status} />{c.assign_reason && <div className="mt-1 text-xs text-muted-foreground">{c.assigned_by}: {c.assign_reason}</div>}</td>
                <td className="px-2 py-2 text-right">
                  {["unattributed", "house"].includes(c.status) && <Button size="sm" variant="outline" onClick={() => setActive(c)} data-testid={`assign-rep-btn-${c.charge_id}`}>Assign rep</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {active && <AssignDialog charge={active} reps={reps} onClose={() => setActive(null)} onDone={() => { setActive(null); onChange(); }} />}
    </div>
  );
};
